import IAppLogger from '@app/lib/IAppLogger';
import { audioDevicesState } from '@modules/SoftPhoneModule/audioDevicesState';
import { DevicesReadyPayload } from '@modules/SoftPhoneModule/types';
import { webPhoneEventNames } from '@modules/SoftPhoneModule/constants/events';
import { ref } from 'valtio';

/**
 * Handles the `webphone:devices-ready` event emitted by the webphone MFE
 * when audio devices have been enumerated and are ready for use.
 *
 * Populates the available device lists and active device IDs in
 * {@link audioDevicesState} and marks devices as ready.
 */
const handleDevicesReady = (
  payload: DevicesReadyPayload,
  logger: IAppLogger,
): void => {
  const {
    audioInputDevices,
    audioOutputDevices,
    activeAudioInputDeviceId,
    activeAudioOutputDeviceId,
    activeRingOutputDeviceId,
    microphoneAnalyser,
  } = payload;

  logger.debug(webPhoneEventNames.devicesReady, {
    activeAudioInputDeviceId,
    activeAudioOutputDeviceId,
    activeRingOutputDeviceId,
  });

  audioDevicesState.audioInputDevices = [...audioInputDevices];
  audioDevicesState.audioOutputDevices = [...audioOutputDevices];
  audioDevicesState.currentInputDeviceId = activeAudioInputDeviceId;
  audioDevicesState.currentOutputDeviceId = activeAudioOutputDeviceId;
  audioDevicesState.currentRingDeviceId = activeRingOutputDeviceId;
  // AnalyserNode is a native browser object whose methods (e.g.
  // getByteTimeDomainData) break when wrapped in a Valtio proxy because
  // the proxy changes `this` away from the real native instance. ref()
  // tells Valtio to pass the object through as-is in snapshots.
  audioDevicesState.microphoneAnalyser = microphoneAnalyser
    ? ref(microphoneAnalyser)
    : null;
  audioDevicesState.isRingOutputEnabled =
    activeRingOutputDeviceId !== activeAudioOutputDeviceId;
  audioDevicesState.isDevicesReady = true;
  audioDevicesState.errorCategory = null;

  // Reset dismiss flags unconditionally — devicesReady always represents a meaningful state
  // transition. If the MFE fires devicesReady with an empty device list before enumeration
  // completes, the cosmetic flash of Scenario 2 self-corrects when handleDeviceListChanged
  // arrives with real devices. The alternative (conditional reset) permanently suppresses the
  // banner for agents whose session genuinely starts with no mic, which has no recovery path.
  audioDevicesState.dismissedInitFailed = false;
  audioDevicesState.dismissedHotUnplug = false;
};

export default handleDevicesReady;
